import { EqlFiltersAutoComplete } from '@modules/common/components/EqlFiltersAutoComplete';
import { SuggestionOption } from '@modules/common/components/MultiColumnAutoComplete/MultiColumnAutoCompleteExample/types';
import { getFixedWhereClause } from '@modules/common/utils';
import { QueryBuilderActions } from '@modules/queryBuilder/components/QueryBuilderActions';
import {
  denormaliseQuery,
  getEqlFieldByFieldID,
  getFilterNodeFromSuggestion,
  isFilterValueEmpty,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { useFetchCustomFilter } from '@modules/queryBuilder/hooks';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { QueryOnChangeProps } from '@modules/queryBuilder/types';
import { ButtonProps, TextField, TextFieldProps } from '@mui/material';
import { EqlField, EqlFieldValue, EqlFieldValueType } from 'generated/graphql';
import { useCallback, useEffect } from 'react';
import toWhereClause from 'v2/core/services/queryBuilder/toWhereClause';

export interface QueryBuilderTextFieldProps {
  onExecute?: (params: QueryOnChangeProps) => void;
  autoFocus?: boolean;
  forceEnableExecuteButton?: boolean;
  openOnFocus?: boolean;
  shouldRenderExecuteButton?: boolean;
  buttonColor: ButtonProps['color'];
  inputProps?: TextFieldProps['InputProps'];
}

export const QueryBuilderTextField = ({
  onExecute,
  autoFocus = false,
  forceEnableExecuteButton = false,
  openOnFocus = true,
  shouldRenderExecuteButton = true,
  buttonColor,
  inputProps,
}: QueryBuilderTextFieldProps) => {
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);
  const rootID = useQueryBuilderStore(({ query }) => query.rootID);
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const eqlFieldByFieldID = useQueryBuilderStore(
    ({ metadata }) => metadata.eqlFieldByFieldID,
  );

  const [fetchAndApplyFilters] = useFetchCustomFilter();

  const addFilter = async ({
    eqlField,
    eqlFieldValue,
  }: {
    eqlField: EqlField;
    eqlFieldValue?: EqlFieldValue;
  }) => {
    if (eqlField.valueType === EqlFieldValueType.None) {
      const analysisID = eqlField.detail?.reference?.referenceID;
      await fetchAndApplyFilters(analysisID);
      return;
    }
    const updatedFilter = getFilterNodeFromSuggestion({
      option: {
        field: eqlField,
        value: eqlFieldValue,
        isCompleteMatch: false,
      },
      parent: rootID,
    });

    const updatedNodes = upsertQueryFilterNode({
      nodesByID: nodes,
      parentID: rootID,
      node: updatedFilter,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });

    const updatedEqlFieldByFieldID = getEqlFieldByFieldID({
      eqlField,
      eqlFieldByFieldID,
      eqlFieldValue,
    });

    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        filterIDUnderInspect: isFilterValueEmpty(updatedFilter) ? rootID : null,
        shouldFocusExecuteCTA: !!eqlFieldValue,
        eqlFieldByFieldID: updatedEqlFieldByFieldID,
      },
    });

    return updatedNodes;
  };

  const onQueryBuilderTextFieldExecute = async ({
    queryOnChangeProps,
    activeOption,
    textFieldProps,
  }: {
    queryOnChangeProps: QueryOnChangeProps;
    activeOption: SuggestionOption | undefined;
    textFieldProps?: TextFieldProps;
  }) => {
    if (!activeOption) {
      return;
    }

    const updatedNodes = await addFilter({
      eqlField: activeOption?.field,
      eqlFieldValue: activeOption?.value,
    });

    if (!updatedNodes || !textFieldProps?.value) {
      onExecute?.(queryOnChangeProps);
      return;
    }

    const updatedWhereClause = getFixedWhereClause(
      toWhereClause(denormaliseQuery(updatedNodes, rootID)),
    );
    onExecute?.({ whereClause: updatedWhereClause });
  };

  const clearFilterIDUnderInspect = () => {
    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        filterIDUnderInspect: null,
      },
    });
  };

  const makeQBInactive = () => {
    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        isActive: false,
      },
    });
  };

  const onLoadingChange = useCallback(
    (isLoading: boolean) => {
      dispatch({
        type: QueryBuilderActionType.SET_METADATA,
        payload: {
          shouldDisableExecuteCTA: isLoading,
        },
      });
    },
    [dispatch],
  );

  useEffect(() => {
    return () => {
      dispatch({
        type: QueryBuilderActionType.SET_METADATA,
        payload: {
          shouldDisableExecuteCTA: false,
        },
      });
    };
  }, [dispatch]);

  return (
    <EqlFiltersAutoComplete
      className="qb-text-field-autocomplete"
      onChange={addFilter}
      onClose={() => {
        clearFilterIDUnderInspect();
        makeQBInactive();
      }}
      openOnFocus={openOnFocus}
      onLoadingChange={onLoadingChange}
      renderInput={(textFieldProps, activeOption) => (
        <TextField
          {...textFieldProps}
          fullWidth
          placeholder="Search by your feature, reason for feedback, or metadata"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck="false"
          autoFocus={autoFocus}
          variant="outlined"
          InputProps={{
            inputProps: {
              sx: { padding: 2 },
            },
            ...inputProps,
            sx: {
              borderRadius: 1,
              ...inputProps?.sx,
            },
            endAdornment: (
              <QueryBuilderActions
                onExecute={(queryOnChangeProps) =>
                  onQueryBuilderTextFieldExecute({
                    queryOnChangeProps,
                    textFieldProps,
                    activeOption,
                  })
                }
                forceEnableExecuteButton={forceEnableExecuteButton}
                id="autocomplete-primary-btn"
                shouldRenderExecuteButton={shouldRenderExecuteButton}
                buttonColor={buttonColor}
              />
            ),
          }}
        />
      )}
    />
  );
};
